import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const MAX_CUSTOM_VIEWS = 5;

export interface CustomView {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  html_content: string;
  explanation: string | null;
  size: 'full' | 'half';
  display_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Fetches all custom views for a user, ordered by display_order.
 */
export function useCustomViews(userId?: string | null) {
  return useQuery({
    queryKey: ["custom-views", userId],
    queryFn: async (): Promise<CustomView[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("custom_views")
        .select("*")
        .eq("user_id", userId)
        .order("display_order", { ascending: true });

      if (error) {
        console.error("[useCustomViews] Error:", error);
        throw error;
      }

      return (data as any[]) || [];
    },
    enabled: !!userId,
    staleTime: 60_000,
  });
}

/**
 * Save (insert) a new custom view.
 */
export function useSaveCustomView(userId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (view: {
      title: string;
      description?: string | null;
      html_content: string;
      explanation?: string | null;
      size?: 'full' | 'half';
    }) => {
      if (!userId) throw new Error("No user ID");

      // Check widget count before inserting
      const { count, error: countError } = await supabase
        .from("custom_views")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId);

      if (countError) throw countError;

      if ((count ?? 0) >= MAX_CUSTOM_VIEWS) {
        throw new Error(`max_views_reached:Maximum of ${MAX_CUSTOM_VIEWS} custom widgets allowed. Please delete one before adding a new one.`);
      }

      // Get the next display_order
      const { data: existing } = await supabase
        .from("custom_views")
        .select("display_order")
        .eq("user_id", userId)
        .order("display_order", { ascending: false })
        .limit(1);

      const nextOrder =
        existing && existing.length > 0
          ? (existing[0] as any).display_order + 1
          : 0;

      const { data, error } = await supabase
        .from("custom_views")
        .insert({
          user_id: userId,
          title: view.title,
          description: view.description || null,
          html_content: view.html_content,
          explanation: view.explanation || null,
          size: view.size || 'full',
          display_order: nextOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return data as any as CustomView;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-views", userId] });
    },
  });
}

/**
 * Update an existing custom view's HTML/title.
 */
export function useUpdateCustomView(userId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      viewId: string;
      title?: string;
      description?: string | null;
      html_content?: string;
      explanation?: string | null;
      size?: 'full' | 'half';
    }) => {
      const updates: any = { updated_at: new Date().toISOString() };
      if (params.title !== undefined) updates.title = params.title;
      if (params.description !== undefined)
        updates.description = params.description;
      if (params.html_content !== undefined)
        updates.html_content = params.html_content;
      if (params.explanation !== undefined)
        updates.explanation = params.explanation;
      if (params.size !== undefined) updates.size = params.size;

      const { error } = await supabase
        .from("custom_views")
        .update(updates)
        .eq("id", params.viewId)
        .eq("user_id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-views", userId] });
    },
  });
}

/**
 * Delete a custom view.
 */
export function useDeleteCustomView(userId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (viewId: string) => {
      const { error } = await supabase
        .from("custom_views")
        .delete()
        .eq("id", viewId)
        .eq("user_id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-views", userId] });
    },
  });
}

/**
 * Swap the display_order of two custom views (for reordering).
 */
export function useReorderCustomViews(userId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      viewId1: string;
      order1: number;
      viewId2: string;
      order2: number;
    }) => {
      const [r1, r2] = await Promise.all([
        supabase
          .from("custom_views")
          .update({ display_order: params.order2 })
          .eq("id", params.viewId1)
          .eq("user_id", userId),
        supabase
          .from("custom_views")
          .update({ display_order: params.order1 })
          .eq("id", params.viewId2)
          .eq("user_id", userId),
      ]);
      if (r1.error) throw r1.error;
      if (r2.error) throw r2.error;
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ["custom-views", userId] });
      const previous = queryClient.getQueryData<CustomView[]>(["custom-views", userId]);

      if (previous) {
        const updated = previous
          .map((v) => {
            if (v.id === params.viewId1) return { ...v, display_order: params.order2 };
            if (v.id === params.viewId2) return { ...v, display_order: params.order1 };
            return v;
          })
          .sort((a, b) => a.display_order - b.display_order);

        queryClient.setQueryData(["custom-views", userId], updated);
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["custom-views", userId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-views", userId] });
    },
  });
}
